import { App, Descriptions, Flex, Modal } from "antd";
import dayjs from "dayjs";
import { FC, useCallback, useEffect, useState } from "react";
import { backendDateFormat, displayDateFormat } from "tm-shared/constants";
import { Coords } from "tm-shared/types";
import { createUniqueId, encodeJsonToBase64 } from "tm-shared/utils";

import rawLogo from "@/assets/icons/logo-with-text-dark.svg?raw";
import { useAppSelector } from "@/hooks/redux";
import { getPreciseLocation } from "@/utils/geo/geo";

import {
    StyledContentBody,
    StyledDescriptions,
    StyledModalTitle,
    StyledQRCode,
} from "./Styled";
const base64Logo = `data:image/svg+xml;base64,${btoa(rawLogo)}`;

interface QRModalProps {
    open: boolean;
    onClose: () => void;
    pointId: string;
    pointName: string;
}

export const QrModal: FC<QRModalProps> = ({
    onClose,
    open,
    pointId,
    pointName,
}) => {
    const { notification } = App.useApp();

    const { lastName, firstName, roleName, id } = useAppSelector((s) => s.user);

    const [isLoading, setIsLoading] = useState(false);
    const [qrData, setQrData] = useState<string | null>(null);

    const generateQR = useCallback(async () => {
        setIsLoading(true);
        setQrData(null);

        let coords: Coords | null = null;

        try {
            coords = await getPreciseLocation();
        } catch (error) {
            const geoErr = error as GeolocationPositionError;
            if (geoErr && typeof geoErr === "object" && "code" in geoErr) {
                if (geoErr?.code === 1) {
                    notification.warning({
                        title: "Доступ до геолокації не дозволено",
                    });
                }
            }
        }

        const data = {
            id: createUniqueId(),
            pointId,
            generatedAt: dayjs().format(backendDateFormat.dateTime),
            managerId: id,
            geo: coords,
        };

        setQrData(encodeJsonToBase64(data));
        setIsLoading(false);
    }, [notification, id, pointId]);

    useEffect(() => {
        if (open && !qrData && !isLoading) {
            generateQR();
        }
    }, [generateQR, isLoading, open, qrData]);

    const handleClose = () => {
        setQrData(null);
        setIsLoading(false);
        onClose();
    };

    return (
        <Modal
            title={
                <StyledModalTitle>
                    <div className="title-main">QR-код для перевірки</div>
                    <div className="title-subtitle">{pointName}</div>
                </StyledModalTitle>
            }
            open={open}
            onCancel={handleClose}
            centered
            footer={null}
            mask={{ closable: false }}
            width={500}
        >
            <StyledContentBody>
                <Flex
                    vertical
                    align="center"
                    justify="center"
                    gap={20}
                    style={{ padding: "20px" }}
                >
                    <StyledQRCode
                        value={qrData || "-"}
                        icon={base64Logo}
                        size={280}
                        iconSize={70}
                        status={isLoading ? "loading" : "active"}
                        errorLevel="H"
                    />

                    <StyledDescriptions column={1} colon={false}>
                        <Descriptions.Item label="Співробітник:">
                            {lastName} {firstName}
                        </Descriptions.Item>
                        <Descriptions.Item label="Роль:">
                            {roleName}
                        </Descriptions.Item>
                        <Descriptions.Item label="Час створення:">
                            {isLoading
                                ? "Генерація QR-коду…"
                                : dayjs().format(displayDateFormat.dateTime)}
                        </Descriptions.Item>
                    </StyledDescriptions>
                </Flex>
            </StyledContentBody>
        </Modal>
    );
};
